import type { SiteConfig } from '../types/config';
import type { SlotPlacementId } from '../types/types';

export interface PlacementMinHeight {
  desktop: number;
  mobile: number;
}

const DEFAULT_MIN_HEIGHT: PlacementMinHeight = {
  desktop: 300,
  mobile: 630,
};

const BELOW_THE_FOLD_MIN_HEIGHT: PlacementMinHeight = {
  desktop: 400,
  mobile: 400,
};

type MultitenantConfig = NonNullable<
  NonNullable<SiteConfig['configs']>['multitenant']
>;

function readV2SlotPlacementMinHeight(
  multitenant: MultitenantConfig | undefined,
): Partial<PlacementMinHeight> | undefined {
  return multitenant?.layout?.slotPlacementMinHeight;
}

function readV1PlacementMinHeight(
  multitenant: MultitenantConfig | undefined,
  placementId: SlotPlacementId,
): Partial<PlacementMinHeight> | undefined {
  return multitenant?.slotPlacement?.[placementId]?.defaultMinHeight;
}

function withDefaults(
  partial: Partial<PlacementMinHeight> | undefined,
  fallback: PlacementMinHeight,
): PlacementMinHeight {
  return {
    desktop: partial?.desktop ?? fallback.desktop,
    mobile: partial?.mobile ?? fallback.mobile,
  };
}

/**
 * Resolves DE placement min-heights from tenant config.
 *
 * Priority:
 * 1. v1 per-placement: multitenant.slotPlacement[placementId].defaultMinHeight
 * 2. v2 global: multitenant.layout.slotPlacementMinHeight
 * 3. Placement-specific platform defaults (belowTheFold)
 * 4. Platform fallback (300 / 630)
 */
export function resolvePlacementMinHeight(
  placementId: SlotPlacementId,
  tenantConfigs: SiteConfig['configs'] | undefined,
): PlacementMinHeight {
  if (!placementId) {
    return DEFAULT_MIN_HEIGHT;
  }

  const multitenant = tenantConfigs?.multitenant;
  const v1Placement = readV1PlacementMinHeight(multitenant, placementId);
  if (v1Placement?.desktop != null || v1Placement?.mobile != null) {
    return withDefaults(v1Placement, DEFAULT_MIN_HEIGHT);
  }

  const v2Global = readV2SlotPlacementMinHeight(multitenant);
  if (v2Global?.desktop != null || v2Global?.mobile != null) {
    return withDefaults(v2Global, DEFAULT_MIN_HEIGHT);
  }

  if (placementId === 'belowTheFold') {
    return BELOW_THE_FOLD_MIN_HEIGHT;
  }

  return DEFAULT_MIN_HEIGHT;
}
